/* DRIVE IQ — ADVENTURE MODE V600.2 SAFE RESUME / NO AUTO PLACE REOPEN
   File: adventure-resume.js

   Purpose:
   - One background/foreground recovery authority for Adventure V600.
   - Restores Mapbox before surrounding UI.
   - Restores camera, results, selected place, and exact UI mode from shared state.
   - Prevents duplicate lifecycle callbacks from fighting each other.
   - Prevents black-screen resume.
   - Restores browse-results shelf when that was the last active mode.
   - Lets Place IQ own its own open transition.
   - Does not create scans, pins, photos, ranking, or routes.

   Load after:
   1. adventure-config.js
   2. adventure-utils.js
   3. adventure-state.js
   4. adventure-events.js
   5. adventure-map.js
   6. adventure-pins.js
   7. adventure-place-card.js
   8. adventure-ui.js
*/

(function (window, document) {
  "use strict";

  if (window.DriveIQAdventureResume) {
    return;
  }

  var config = window.DriveIQAdventureConfig;
  var utils = window.DriveIQAdventureUtils;
  var state = window.DriveIQAdventureState;
  var events = window.DriveIQAdventureEvents;
  var mapController = window.DriveIQAdventureMap;
  var pinsController = window.DriveIQAdventurePins;
  var placeCardController = window.DriveIQAdventurePlaceCard;
  var uiController = window.DriveIQAdventureUI;

  if (
    !config ||
    !utils ||
    !state ||
    !events ||
    !mapController ||
    !pinsController ||
    !placeCardController ||
    !uiController
  ) {
    throw new Error(
      "adventure-resume.js requires config, utils, state, events, map, pins, place-card, and UI."
    );
  }

  var initialized = false;
  var restoring = false;
  var restoreToken = 0;
  var restoreTimer = null;
  var settleTimer = null;
  var bound = false;
  var lastRestoreRequestedAt = 0;
  var lastRestoreReason = "";
  var FOREGROUND_DEBOUNCE_MS = 420;

  function log() {
    if (
      !config.logging.enabled ||
      !window.console ||
      !window.console.log
    ) {
      return;
    }

    var args = Array.prototype.slice.call(arguments);
    args.unshift(config.logging.prefix, "[Resume]");
    window.console.log.apply(window.console, args);
  }

  function now() {
    return Date.now ? Date.now() : new Date().getTime();
  }

  function clearTimers() {
    if (restoreTimer) {
      window.clearTimeout(restoreTimer);
      restoreTimer = null;
    }

    if (settleTimer) {
      window.clearTimeout(settleTimer);
      settleTimer = null;
    }
  }

  function snapshotForStorage() {
    var current = state.snapshot();

    return {
      savedAt: now(),
      camera: current.map.camera || null,
      results: current.results.items || [],
      selectedPlace:
        current.placeCard.item ||
        current.selection.selectedPlace ||
        null,
      uiMode:
        current.ui.mode === "place"
          ? ((current.results.items || []).length ? "browse_results" : "map")
          : (current.ui.mode || "menu"),
      destination: current.destination.committed || null
    };
  }

  function persistSnapshot() {
    if (!config.resume.enabled) {
      return false;
    }

    try {
      utils.lsSet(
        config.storage.resumeSnapshot,
        JSON.stringify(snapshotForStorage())
      );
      return true;
    } catch (error) {
      return false;
    }
  }

  function loadSnapshot() {
    var stored;

    try {
      stored = utils.lsJSON(
        config.storage.resumeSnapshot,
        null
      );
    } catch (error) {
      stored = null;
    }

    if (!stored || !stored.savedAt) {
      return null;
    }

    if (
      now() - Number(stored.savedAt) >
      config.resume.snapshotMaximumAgeMs
    ) {
      return null;
    }

    return stored;
  }

  function applySnapshotToState(snapshot) {
    if (!snapshot) {
      return false;
    }

    state.transaction("resume:apply-snapshot", function () {
      if (snapshot.camera) {
        state.merge("map.camera", snapshot.camera, {
          action: "resume:camera-state"
        });
      }

      if (Array.isArray(snapshot.results)) {
        state.merge("results", {
          items: snapshot.results.slice(0),
          count: snapshot.results.length
        }, {
          action: "resume:results-state"
        });
      }

      /*
       * Safe Resume Authority:
       * Preserve results and camera, but never reactivate a persisted Place IQ
       * selection. A stale selected-place transaction can reopen a black card
       * several hundred milliseconds after the page initially renders.
       */
      if (snapshot.selectedPlace) {
        state.merge("selection", {
          selectedPlace: null,
          source: "resume-cleared-stale-place"
        }, {
          action: "resume:clear-stale-place"
        });

        state.merge("placeCard", {
          item: null,
          open: false
        }, {
          action: "resume:clear-stale-card"
        });
      }

      if (snapshot.destination) {
        state.setDestination(
          snapshot.destination,
          "resume-snapshot",
          true
        );
      }

      if (snapshot.uiMode) {
        try {
          var safeMode =
            snapshot.uiMode === "place"
              ? (snapshot.results && snapshot.results.length
                  ? "browse_results"
                  : "map")
              : snapshot.uiMode;

          if (typeof state.setUIState === "function") {
            state.setUIState(safeMode);
          } else {
            state.merge("ui", {
              mode: safeMode
            }, {
              action: "resume:ui-mode-safe"
            });
          }
        } catch (uiStateError) {}
      }

      state.merge("resume", {
        snapshot: snapshot
      }, {
        action: "resume:snapshot-loaded"
      });
    });

    return true;
  }

  function restoreCamera(camera) {
    if (!camera || !camera.center) {
      return false;
    }

    return mapController.jumpTo({
      center: camera.center,
      zoom: camera.zoom,
      bearing: camera.bearing,
      pitch: camera.pitch
    });
  }

  function restoreBrowseResults() {
    try {
      if (
        uiController &&
        typeof uiController.enforceResultsBrowseState === "function"
      ) {
        uiController.enforceResultsBrowseState();
      }

      if (
        uiController &&
        typeof uiController.showOnlyResultShelf === "function"
      ) {
        return uiController.showOnlyResultShelf() !== false;
      }

      if (
        window.DriveIQAdventureScan &&
        typeof window.DriveIQAdventureScan.restoreResultShelf === "function"
      ) {
        return window.DriveIQAdventureScan.restoreResultShelf(
          "resume-browse-results"
        ) !== false;
      }
    } catch (error) {}

    return false;
  }

  function restoreVisualState(reason, token) {
    var snapshot;
    var results;
    var selectedPlace;
    var uiMode;
    var camera;

    if (token !== restoreToken) {
      return false;
    }

    snapshot = state.get("resume.snapshot", null);
    results = state.get("results.items", []) || [];
    selectedPlace =
      state.get("placeCard.item", null) ||
      state.get("selection.selectedPlace", null);
    uiMode = String(
      state.get(
        "ui.mode",
        snapshot && snapshot.uiMode
          ? snapshot.uiMode
          : "menu"
      ) || "menu"
    );

    camera =
      snapshot && snapshot.camera
        ? snapshot.camera
        : state.get("map.camera", null);

    if (camera) {
      restoreCamera(camera);
    }

    if (Array.isArray(results) && results.length) {
      try {
        pinsController.render(results);
      } catch (pinError) {}
    }

    if (uiMode === "place") {
      /*
       * Never auto-reopen Place IQ during startup, pageshow, visibilitychange,
       * or focus recovery. Restore the safe browse/map surface instead and
       * require a fresh user pin tap for a new Place IQ transaction.
       */
      try {
        state.merge("selection", {
          selectedPlace: null,
          source: "resume-place-suppressed"
        }, {
          action: "resume:suppress-place-reopen"
        });

        state.merge("placeCard", {
          item: null,
          open: false
        }, {
          action: "resume:close-stale-place-card"
        });
      } catch (placeStateError) {}

      if (results.length) {
        restoreBrowseResults();
        return true;
      }

      uiController.showMap({
        force: true
      });
      return true;
    }

    if (
      uiMode === "browse_results" &&
      results.length
    ) {
      return restoreBrowseResults();
    }

    if (uiMode === "map" && results.length) {
      uiController.showMap({
        force: true
      });
      return true;
    }

    uiController.renderMode(
      uiMode || "menu"
    );

    return true;
  }

  function finishRestore(reason, token) {
    if (token !== restoreToken) {
      return false;
    }

    restoring = false;

    state.merge("resume", {
      restoring: false,
      restored: true,
      foregroundedAt: now(),
      restoredAt: now()
    }, {
      action: "resume:complete"
    });

    events.emit("app:resumed", {
      reason: String(reason || "unknown"),
      restoredAt: now()
    });

    log("Restore complete", reason);

    return true;
  }

  function performRestore(reason, token) {
    var snapshot;
    var restoreReason = String(reason || "resume");

    if (token !== restoreToken) {
      return false;
    }

    snapshot = loadSnapshot();

    if (snapshot) {
      applySnapshotToState(snapshot);
    }

    /*
     * One map restore per foreground transaction.
     * Later visual restoration may restore the camera, but never calls
     * mapController.restore() again.
     */
    mapController.restore(
      restoreReason + "-map-first"
    );

    restoreTimer = window.setTimeout(function () {
      restoreTimer = null;

      restoreVisualState(
        restoreReason + "-visual",
        token
      );
    }, Math.max(
      40,
      Number(config.resume.repaintDelayMs || 120)
    ));

    settleTimer = window.setTimeout(function () {
      settleTimer = null;

      if (token !== restoreToken) {
        return;
      }

      /*
       * Final bounded pass only reasserts the visual mode.
       * It does not issue another map restore.
       */
      restoreVisualState(
        restoreReason + "-settled",
        token
      );

      finishRestore(reason, token);
    }, Math.max(
      180,
      Number(config.resume.secondaryRepaintDelayMs || 360)
    ));

    return true;
  }

  function restore(reason) {
    var token;
    var requestedAt = now();
    var restoreReason = String(reason || "resume");

    if (!config.resume.enabled) {
      return false;
    }

    /*
     * visibilitychange, pageshow, and focus often fire together.
     * Coalesce them into one foreground transaction.
     */
    if (
      restoring &&
      requestedAt - lastRestoreRequestedAt <
        FOREGROUND_DEBOUNCE_MS
    ) {
      lastRestoreReason = restoreReason;
      return true;
    }

    clearTimers();

    restoreToken += 1;
    token = restoreToken;
    restoring = true;
    lastRestoreRequestedAt = requestedAt;
    lastRestoreReason = restoreReason;

    state.merge("resume", {
      restoring: true,
      restored: false,
      foregroundedAt: requestedAt
    }, {
      action: "resume:start"
    });

    return performRestore(
      restoreReason,
      token
    );
  }

  function background(reason) {
    persistSnapshot();

    state.merge("resume", {
      backgroundedAt: now(),
      restored: false
    }, {
      action: "resume:background"
    });

    events.emit("app:backgrounded", {
      reason: String(reason || "unknown"),
      backgroundedAt: now()
    });

    return true;
  }

  function bindLifecycle() {
    if (bound) {
      return;
    }

    bound = true;

    document.addEventListener(
      "visibilitychange",
      function () {
        if (document.visibilityState === "hidden") {
          background("visibility-hidden");
        } else if (
          document.visibilityState === "visible"
        ) {
          restore("visibility-visible");
        }
      },
      false
    );

    window.addEventListener(
      "pagehide",
      function () {
        background("pagehide");
      },
      false
    );

    window.addEventListener(
      "pageshow",
      function () {
        restore("pageshow");
      },
      false
    );

    window.addEventListener(
      "blur",
      function () {
        persistSnapshot();
      },
      false
    );

    window.addEventListener(
      "focus",
      function () {
        restore("window-focus");
      },
      false
    );
  }

  function init() {
    var snapshot;

    if (initialized) {
      return true;
    }

    initialized = true;
    bindLifecycle();

    snapshot = loadSnapshot();

    if (snapshot) {
      applySnapshotToState(snapshot);
    }

    events.on("scan:completed", function () {
      persistSnapshot();
    });

    events.on("place:opened", function () {
      persistSnapshot();
    });

    events.on("place:closed", function () {
      persistSnapshot();
    });

    events.on("map:camera-changed", function () {
      persistSnapshot();
    });

    events.on("destination:committed", function () {
      persistSnapshot();
    });

    events.on("results:shelf-shown", function () {
      persistSnapshot();
    });

    log("Resume reliability authority initialized");

    return true;
  }

  function destroy() {
    clearTimers();
    initialized = false;
    restoring = false;
    restoreToken += 1;
    return true;
  }

  window.DriveIQAdventureResume = {
    init: init,
    restore: restore,
    background: background,
    persistSnapshot: persistSnapshot,
    loadSnapshot: loadSnapshot,
    destroy: destroy,
    isRestoring: function () {
      return restoring;
    },
    status: function () {
      return {
        build: "V600_2_SAFE_RESUME_NO_AUTO_PLACE_REOPEN",
        initialized: initialized,
        restoring: restoring,
        restoreToken: restoreToken,
        lastRestoreReason: lastRestoreReason,
        lastRestoreRequestedAt: lastRestoreRequestedAt,
        foregroundDebounceMs: FOREGROUND_DEBOUNCE_MS
      };
    }
  };

  log("Safe resume controller V600.2 loaded");
}(window, document));